import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CardActions, 
  Divider, 
  Paper,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  InputAdornment,
  AppBar,
  Toolbar,
  IconButton,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { 
  CheckCircle, 
  TrendingUp, 
  ShowChart, 
  Autorenew,
  Menu as MenuIcon,
  ArrowForward,
  Mail,
  VerifiedUser,
  Speed,
  Psychology,
  AutoGraph,
  Devices
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

// Mock testimonials
const testimonials = [
  {
    id: 1,
    name: 'Alex Johnson',
    position: 'Retail Trader',
    comment: 'This AI trading platform has completely transformed my trading strategy. The insights are invaluable, and the automation saves me hours every day.',
    avatar: 'https://randomuser.me/api/portraits/men/32.jpg'
  },
  {
    id: 2,
    name: 'Sarah Williams',
    position: 'Day Trader',
    comment: 'After trying numerous trading platforms, this one stands out with its AI capabilities. The predictive analytics have improved my win rate significantly.',
    avatar: 'https://randomuser.me/api/portraits/women/44.jpg'
  },
  {
    id: 3,
    name: 'Michael Chen',
    position: 'Investment Advisor',
    comment: 'I recommend this platform to all my clients. The risk management features and AI-driven strategies create a perfect balance of innovation and safety.',
    avatar: 'https://randomuser.me/api/portraits/men/67.jpg'
  }
];

// Mock pricing plans
const pricingPlans = [
  {
    id: 'basic',
    name: 'Basic',
    price: 29,
    period: 'month',
    description: 'Perfect for beginners and casual traders',
    features: [
      'AI-powered market analysis',
      'Basic strategy templates',
      'Real-time data visualization',
      'Standard technical indicators',
      'Email support'
    ],
    limitations: [
      'Up to 5 active strategies',
      'Standard execution speed',
      'Daily strategy updates'
    ],
    buttonText: 'Start Free Trial',
    highlighted: false
  },
  {
    id: 'pro',
    name: 'Professional',
    price: 79,
    period: 'month',
    description: 'For serious traders seeking an edge',
    features: [
      'Everything in Basic',
      'Advanced AI signal generation',
      'Custom strategy builder',
      'Priority execution',
      'Risk management suite',
      'API access',
      'Priority support'
    ],
    limitations: [
      'Up to 20 active strategies',
      'Hourly strategy updates'
    ],
    buttonText: 'Get Started',
    highlighted: true
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 199,
    period: 'month',
    description: 'For professional traders and firms',
    features: [
      'Everything in Professional',
      'Unlimited active strategies',
      'Real-time strategy updates',
      'Custom AI model training',
      'White-label options',
      'Dedicated account manager',
      'Custom integrations',
      '24/7 premium support'
    ],
    limitations: [],
    buttonText: 'Contact Sales',
    highlighted: false
  }
];

// FAQ data
const faqs = [
  {
    question: 'How does the AI trading system work?',
    answer: 'Our platform uses advanced machine learning algorithms to analyze market patterns, predict trends, and generate trading signals. It continuously learns from market data to improve its predictions and adapts to changing market conditions.'
  },
  {
    question: 'Do I need trading experience to use this platform?',
    answer: 'No, our platform is designed for traders of all experience levels. Beginners can use pre-built strategies and learn through our comprehensive documentation, while experienced traders can customize and fine-tune their own strategies.'
  },
  {
    question: 'What markets can I trade with this system?',
    answer: 'Our platform supports multiple markets including stocks, forex, cryptocurrencies, and commodities. Each subscription tier offers different levels of market access.'
  },
  {
    question: 'How secure is my trading data?',
    answer: 'Security is our top priority. We use enterprise-grade encryption for all data, implement strict access controls, and never share your trading data with third parties.'
  },
  {
    question: 'Can I cancel my subscription anytime?',
    answer: 'Yes, you can cancel your subscription at any time. We offer a 14-day money-back guarantee for new users to try our platform risk-free.'
  }
];

// Features list
const features = [
  {
    title: 'AI-Powered Analysis',
    description: 'Our advanced AI algorithms analyze market data to identify patterns and trends that humans might miss.',
    icon: <Psychology fontSize="large" color="primary" />
  },
  {
    title: 'Predictive Analytics',
    description: 'Get ahead of market movements with predictive indicators based on machine learning.',
    icon: <AutoGraph fontSize="large" color="primary" />
  },
  {
    title: 'Risk Management',
    description: 'Protect your capital with automated risk controls and position sizing optimizations.',
    icon: <VerifiedUser fontSize="large" color="primary" />
  },
  {
    title: 'Real-time Execution',
    description: 'Execute trades with lightning speed through our optimized trading infrastructure.',
    icon: <Speed fontSize="large" color="primary" />
  },
  {
    title: 'Multi-platform Access',
    description: 'Access your trading dashboard from any device, anywhere in the world.',
    icon: <Devices fontSize="large" color="primary" />
  },
  {
    title: 'Strategy Builder',
    description: 'Create custom trading strategies with our intuitive drag-and-drop interface.',
    icon: <TrendingUp fontSize="large" color="primary" />
  }
];

const LandingPage = () => {
  const [email, setEmail] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    // In a real app, this would send the email to your API
    console.log('Subscribed with email:', email);
    setEmail('');
    alert('Thanks for subscribing! We\'ll keep you updated with the latest news.');
  };

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Navigation bar */}
      <AppBar position="static" color="transparent" elevation={0} sx={{ mb: 0 }}>
        <Container maxWidth="lg">
          <Toolbar disableGutters>
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}
            >
              <ShowChart color="primary" sx={{ mr: 1 }} />
              <Typography variant="h5" component="span" color="primary" fontWeight="bold">
                AI Trading
              </Typography>
            </Typography>

            {isMobile ? (
              <IconButton color="inherit">
                <MenuIcon />
              </IconButton>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Button color="inherit" component={RouterLink} to="/documentation">
                  Documentation
                </Button>
                <Button color="inherit" component={RouterLink} to="/login">
                  Login
                </Button>
                <Button 
                  variant="contained" 
                  color="primary" 
                  component={RouterLink} 
                  to="/register"
                  sx={{ ml: 2 }}
                >
                  Sign Up
                </Button>
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Hero section */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: 'auto', md: '85vh' },
          display: 'flex',
          alignItems: 'center',
          backgroundImage: 'radial-gradient(circle at 30% 50%, rgba(63, 81, 181, 0.1), transparent)',
          pb: { xs: 8, md: 0 }
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ py: { xs: 4, md: 0 } }}>
                <Typography
                  variant="h2"
                  component="h1"
                  fontWeight="bold"
                  gutterBottom
                  sx={{
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                  }}
                >
                  Trade Smarter with
                  <Box component="span" sx={{ color: 'primary.main', display: 'inline' }}> AI-Powered</Box> Precision
                </Typography>
                <Typography
                  variant="h5"
                  color="textSecondary"
                  paragraph
                  sx={{ mb: 4, maxWidth: '90%' }}
                >
                  Harness the power of artificial intelligence to optimize your trading strategies, reduce risks, and maximize returns.
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    component={RouterLink}
                    to="/register"
                    sx={{ px: 4, py: 1.5, borderRadius: 2 }}
                  >
                    Start Free Trial
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="large"
                    component={RouterLink}
                    to="/documentation"
                    sx={{ px: 4, py: 1.5, borderRadius: 2 }}
                  >
                    Learn More
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 4 }}>
                  <Chip 
                    icon={<VerifiedUser />} 
                    label="No credit card required" 
                    variant="outlined" 
                    sx={{ mr: 2 }} 
                  />
                  <Chip 
                    icon={<Autorenew />} 
                    label="14-day free trial" 
                    variant="outlined" 
                  />
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box 
                component="img"
                src="https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1740&q=80"
                alt="Trading Dashboard"
                sx={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: 2,
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
                  transform: { xs: 'none', md: 'perspective(1000px) rotateY(-5deg)' },
                  transition: 'transform 0.5s',
                  '&:hover': {
                    transform: { xs: 'none', md: 'perspective(1000px) rotateY(0deg)' }
                  }
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box sx={{ py: 10, backgroundColor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h6" color="primary" gutterBottom>
              POWERFUL FEATURES
            </Typography>
            <Typography variant="h3" component="h2" fontWeight="bold" gutterBottom>
              Why Choose Our AI Trading Platform
            </Typography>
            <Typography variant="body1" color="textSecondary" sx={{ maxWidth: 700, mx: 'auto' }}>
              Discover the innovative features that set our platform apart from traditional trading systems.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card 
                  elevation={0} 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    borderRadius: 2,
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)'
                    }
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, p: 4 }}>
                    <Box sx={{ mb: 2 }}>
                      {feature.icon}
                    </Box>
                    <Typography variant="h5" component="h3" gutterBottom fontWeight="bold">
                      {feature.title}
                    </Typography>
                    <Typography variant="body1" color="textSecondary">
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Pricing Section */}
      <Box sx={{ py: 10 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h6" color="primary" gutterBottom>
              PRICING PLANS
            </Typography>
            <Typography variant="h3" component="h2" fontWeight="bold" gutterBottom>
              Choose the Right Plan for You
            </Typography>
            <Typography variant="body1" color="textSecondary" sx={{ maxWidth: 700, mx: 'auto' }}>
              Flexible pricing options designed to accommodate traders of all levels, from beginners to professionals.
            </Typography>
          </Box>

          <Grid container spacing={4} justifyContent="center">
            {pricingPlans.map((plan) => (
              <Grid item xs={12} sm={6} md={4} key={plan.id}>
                <Card 
                  elevation={plan.highlighted ? 8 : 1} 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    borderRadius: 2,
                    position: 'relative',
                    overflow: 'visible',
                    border: plan.highlighted ? `2px solid ${theme.palette.primary.main}` : 'none',
                    transform: plan.highlighted ? 'scale(1.05)' : 'none',
                    zIndex: plan.highlighted ? 2 : 1,
                    transition: 'transform 0.3s'
                  }}
                >
                  {plan.highlighted && (
                    <Chip 
                      label="Most Popular" 
                      color="primary" 
                      sx={{ 
                        position: 'absolute', 
                        top: -15, 
                        left: '50%', 
                        transform: 'translateX(-50%)'
                      }} 
                    />
                  )}
                  <CardContent sx={{ p: 4, flexGrow: 1 }}>
                    <Typography variant="h5" component="h3" fontWeight="bold" gutterBottom>
                      {plan.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 2 }}>
                      <Typography variant="h3" component="span" fontWeight="bold">
                        ${plan.price}
                      </Typography>
                      <Typography variant="body1" component="span" color="textSecondary">
                        /{plan.period}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="textSecondary" paragraph>
                      {plan.description}
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      What's included:
                    </Typography>
                    <List disablePadding>
                      {plan.features.map((feature, index) => (
                        <ListItem disablePadding key={index} sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 30 }}>
                            <CheckCircle fontSize="small" color="primary" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={feature} 
                            primaryTypographyProps={{ variant: 'body2' }} 
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                  <CardActions sx={{ p: 3, pt: 0 }}>
                    <Button 
                      fullWidth 
                      variant={plan.highlighted ? "contained" : "outlined"} 
                      color="primary"
                      component={RouterLink}
                      to="/register"
                      size="large"
                    >
                      {plan.buttonText}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Testimonials */}
      <Box sx={{ py: 10, backgroundColor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h6" color="primary" gutterBottom>
              TESTIMONIALS
            </Typography>
            <Typography variant="h3" component="h2" fontWeight="bold" gutterBottom>
              What Our Users Say
            </Typography>
            <Typography variant="body1" color="textSecondary" sx={{ maxWidth: 700, mx: 'auto' }}>
              Hear from traders who have transformed their trading experience with our AI platform.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {testimonials.map((testimonial) => (
              <Grid item xs={12} md={4} key={testimonial.id}>
                <Card 
                  elevation={0} 
                  sx={{ 
                    height: '100%', 
                    borderRadius: 2,
                    p: 3,
                    transition: 'transform 0.3s',
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)'
                    }
                  }}
                >
                  <CardContent>
                    <Typography variant="body1" paragraph sx={{ fontStyle: 'italic' }}>
                      "{testimonial.comment}"
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        component="img"
                        src={testimonial.avatar}
                        alt={testimonial.name}
                        sx={{
                          width: 50,
                          height: 50,
                          borderRadius: '50%',
                          mr: 2
                        }}
                      />
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {testimonial.name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {testimonial.position}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ py: 10 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h6" color="primary" gutterBottom>
              FREQUENTLY ASKED QUESTIONS
            </Typography>
            <Typography variant="h3" component="h2" fontWeight="bold" gutterBottom>
              Have Questions? We've Got Answers.
            </Typography>
            <Typography variant="body1" color="textSecondary" sx={{ maxWidth: 700, mx: 'auto' }}>
              Find quick answers to common questions about our AI trading platform.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {faqs.map((faq, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 3, 
                    borderRadius: 2,
                    transition: 'transform 0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.05)'
                    }
                  }}
                >
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {faq.question}
                  </Typography>
                  <Typography variant="body1" color="textSecondary">
                    {faq.answer}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box 
        sx={{ 
          py: 10, 
          backgroundColor: 'primary.main',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Background decorative elements */}
        <Box 
          sx={{ 
            position: 'absolute', 
            top: -100, 
            right: -100, 
            width: 300, 
            height: 300, 
            borderRadius: '50%', 
            backgroundColor: 'rgba(255, 255, 255, 0.1)' 
          }} 
        />
        <Box 
          sx={{ 
            position: 'absolute', 
            bottom: -150, 
            left: -150, 
            width: 400, 
            height: 400, 
            borderRadius: '50%', 
            backgroundColor: 'rgba(255, 255, 255, 0.05)' 
          }} 
        />

        <Container maxWidth="md" sx={{ position: 'relative' }}>
          <Box sx={{ textAlign: 'center', color: 'white' }}>
            <Typography variant="h3" component="h2" fontWeight="bold" gutterBottom>
              Ready to Transform Your Trading?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
              Join thousands of traders who are already using AI to gain an edge in the markets.
            </Typography>
            <Button 
              variant="contained" 
              color="secondary" 
              size="large" 
              component={RouterLink}
              to="/register"
              sx={{ 
                px: 5, 
                py: 1.5, 
                borderRadius: 2,
                backgroundColor: 'white',
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.9)'
                }
              }}
            >
              Start Your Free Trial
            </Button>
            <Typography variant="body2" sx={{ mt: 2, opacity: 0.7 }}>
              No credit card required. 14-day free trial.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Newsletter Section */}
      <Box sx={{ py: 8, backgroundColor: theme.palette.background.default }}>
        <Container maxWidth="md">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
                Stay Updated
              </Typography>
              <Typography variant="body1" paragraph color="textSecondary">
                Subscribe to our newsletter for the latest updates, trading tips, and market insights.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <form onSubmit={handleNewsletterSubmit}>
                <Box sx={{ display: 'flex' }}>
                  <TextField
                    fullWidth
                    placeholder="Enter your email"
                    variant="outlined"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    type="email"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Mail color="action" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mr: 1 }}
                  />
                  <Button 
                    type="submit" 
                    variant="contained" 
                    color="primary"
                    endIcon={<ArrowForward />}
                  >
                    Subscribe
                  </Button>
                </Box>
              </form>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Footer */}
      <Box 
        component="footer" 
        sx={{ 
          backgroundColor: theme.palette.background.paper,
          borderTop: `1px solid ${theme.palette.divider}`,
          py: 6
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ShowChart color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" color="primary" fontWeight="bold">
                  AI Trading
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary" paragraph>
                Next-generation trading platform powered by artificial intelligence. Optimize your strategies, minimize risks, and maximize returns.
              </Typography>
              <Box sx={{ mt: 2 }}>
                {/* Social media icons would go here */}
              </Box>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Platform
              </Typography>
              <List disablePadding>
                {['Features', 'Pricing', 'Documentation', 'API'].map((item) => (
                  <ListItem key={item} disablePadding sx={{ py: 0.5 }}>
                    <Button 
                      color="inherit" 
                      sx={{ color: 'text.secondary', fontWeight: 'normal', p: 0 }}
                    >
                      {item}
                    </Button>
                  </ListItem>
                ))}
              </List>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Company
              </Typography>
              <List disablePadding>
                {['About Us', 'Blog', 'Careers', 'Contact'].map((item) => (
                  <ListItem key={item} disablePadding sx={{ py: 0.5 }}>
                    <Button 
                      color="inherit" 
                      sx={{ color: 'text.secondary', fontWeight: 'normal', p: 0 }}
                    >
                      {item}
                    </Button>
                  </ListItem>
                ))}
              </List>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Legal
              </Typography>
              <List disablePadding>
                {['Terms', 'Privacy', 'Security', 'Compliance'].map((item) => (
                  <ListItem key={item} disablePadding sx={{ py: 0.5 }}>
                    <Button 
                      color="inherit" 
                      sx={{ color: 'text.secondary', fontWeight: 'normal', p: 0 }}
                    >
                      {item}
                    </Button>
                  </ListItem>
                ))}
              </List>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Support
              </Typography>
              <List disablePadding>
                {['Help Center', 'Community', 'Status', 'FAQ'].map((item) => (
                  <ListItem key={item} disablePadding sx={{ py: 0.5 }}>
                    <Button 
                      color="inherit" 
                      sx={{ color: 'text.secondary', fontWeight: 'normal', p: 0 }}
                    >
                      {item}
                    </Button>
                  </ListItem>
                ))}
              </List>
            </Grid>
          </Grid>
          <Box sx={{ mt: 5 }}>
            <Divider />
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' } }}>
              <Typography variant="body2" color="textSecondary">
                &copy; {new Date().getFullYear()} AI Trading System. All rights reserved.
              </Typography>
              <Box>
                <Button color="inherit" size="small" sx={{ color: 'text.secondary' }}>
                  Terms
                </Button>
                <Button color="inherit" size="small" sx={{ color: 'text.secondary' }}>
                  Privacy
                </Button>
                <Button color="inherit" size="small" sx={{ color: 'text.secondary' }}>
                  Cookies
                </Button>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;
